import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { api } from '@services';
import { useAuth } from '@features/auth/context/AuthContext';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

const LoginDialog: React.FC<LoginDialogProps> = ({ open, onClose, onLoginSuccess }) => {
  const { login } = useAuth();
  const [loginEmail, setLoginEmail] = useState<string>('');
  const [loginPassword, setLoginPassword] = useState<string>('');
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('Email and password are required');
      return;
    }

    setLoginLoading(true);
    setLoginError(null);

    try {
      const response = await api.post('/auth/login', {
        email: loginEmail,
        password: loginPassword
      });

      // First check if we have a token
      if (!response.data.token) {
        throw new Error('No token received from server');
      }

      // Ensure token is a string and has a reasonable length
      const token = response.data.token;
      if (typeof token !== 'string' || token.length < 20) {
        console.error('Invalid token format received:', typeof token, 'length:', token?.length || 0);
        throw new Error('Invalid authentication token received');
      }

      // Validate token format (should be a JWT with three parts)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Token does not appear to be valid JWT format (expected 3 parts, got', parts.length, ')');
        throw new Error('Invalid token format');
      }

      // Debug token info
      console.log(`Login success! Token received (length: ${token.length})`);
      try {
        // Simple JWT parts check - doesn't validate signature
        const [header, payload, signature] = token.split('.');
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        console.log('Token payload:', {
          exp: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : 'missing',
          iat: decoded.iat ? new Date(decoded.iat * 1000).toISOString() : 'missing',
          expiresIn: decoded.exp ? Math.floor((decoded.exp * 1000 - Date.now()) / 1000) + 's' : 'unknown',
          userId: decoded.userId || decoded.sub || 'missing',
        });

        // Check for token expiration
        if (decoded.exp && decoded.exp < Math.floor(Date.now() / 1000)) {
          console.error('Token is already expired!');
          throw new Error('Token expired');
        }
      } catch (e) {
        console.warn('Error decoding token', e);
        // Continue anyway - the backend will validate the token
      }
      // Replace localStorage.setItem with AuthContext login
      await login(response.data.token, loginEmail);

      // Clear login form
      setLoginEmail('');
      setLoginPassword('');

      // Notify parent component
      onLoginSuccess();

      // Close dialog
      onClose();
    } catch (error: any) {
      console.error('Login error:', error);
      setLoginError(error.response?.data?.error || error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Login Required</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Authentication is required to analyze this hotel. Please log in to continue.
        </DialogContentText>

        {loginError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loginError}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          id="email"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          value={loginEmail}
          onChange={(e) => setLoginEmail(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          id="password"
          label="Password"
          type="password"
          fullWidth
          variant="outlined"
          value={loginPassword}
          onChange={(e) => setLoginPassword(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleLogin}
          variant="contained"
          disabled={loginLoading}
          endIcon={loginLoading ? <CircularProgress size={16} /> : null}
        >
          Login
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginDialog;